import { forwardRef, type ButtonHTMLAttributes } from 'react';
import type { LucideIcon } from 'lucide-react';
import { Loader2 } from 'lucide-react';

export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'outline'
  | 'ghost'
  | 'tint'
  | 'green'
  | 'danger'
  | 'danger-outline';
export type ButtonSize = 'sm' | 'md' | 'lg';

const base =
  'inline-flex items-center justify-center gap-2 font-semibold transition duration-[180ms] ease-out ' +
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-surface ' +
  'disabled:cursor-not-allowed select-none whitespace-nowrap';

// Checkover handoff: one filled accent button per screen (the thing you came to do);
// secondary is a white fill with a 1.5px strong edge; destructive is outlined, never
// filled — `danger` (filled red) is reserved for permanent deletion. Disabled is its
// own fill, not a faded primary, so a blocked action still reads clearly.
const variants: Record<ButtonVariant, string> = {
  primary: 'bg-accent text-onaccent hover:brightness-[1.07] active:brightness-95 disabled:bg-disabled disabled:text-disabled-ink disabled:brightness-100',
  secondary: 'bg-elev text-ink shadow-[inset_0_0_0_1.5px_var(--strong)] hover:bg-accenttint disabled:opacity-50',
  outline: 'bg-elev text-ink shadow-[inset_0_0_0_1.5px_var(--strong)] hover:bg-accenttint disabled:opacity-50',
  ghost: 'text-ink2 hover:bg-hover hover:text-ink disabled:opacity-50',
  tint: 'bg-accenttint text-accent hover:brightness-105 disabled:opacity-50',
  green: 'bg-success-fill text-white hover:brightness-110 disabled:opacity-50',
  danger: 'bg-dangerfill text-white hover:brightness-110 disabled:opacity-50',
  'danger-outline': 'bg-transparent text-st-cancelled shadow-[inset_0_0_0_1.5px_var(--pill-cancelled-dot)] hover:bg-[color-mix(in_srgb,var(--pill-cancelled-dot)_8%,transparent)] disabled:opacity-50',
};

// Desktop "Standard" floors from the handoff: 48 primary, 44 secondary control.
const sizes: Record<ButtonSize, string> = {
  sm: 'h-10 rounded-[10px] px-3.5 text-[14.5px]',
  md: 'h-11 rounded-[12px] px-4 text-[15px]',
  lg: 'h-12 rounded-[12px] px-5 text-[16px]',
};

const iconSizes: Record<ButtonSize, number> = { sm: 16, md: 18, lg: 18 };

export function buttonStyles(
  variant: ButtonVariant = 'primary',
  size: ButtonSize = 'md',
  extra = ''
): string {
  return `${base} ${variants[variant]} ${sizes[size]} ${extra}`.trim();
}

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: LucideIcon;
  iconRight?: LucideIcon;
  loading?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  function Button(
    { variant = 'primary', size = 'md', icon: Icon, iconRight: IconRight, loading = false, className = '', children, disabled, ...rest },
    ref
  ) {
    const s = iconSizes[size];
    return (
      <button ref={ref} className={buttonStyles(variant, size, className)} disabled={disabled || loading} {...rest}>
        {loading ? <Loader2 size={s} className="animate-spin" /> : Icon && <Icon size={s} />}
        {children}
        {!loading && IconRight && <IconRight size={s} />}
      </button>
    );
  }
);
